import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import PerformanceGraph from '../components/PerformanceGraph';
import ScrollableDropdown from '../components/ScrollableDropdown';

const ProjectList = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAppId, setSelectedAppId] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [appStats, setAppStats] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedStatusGroup, setSelectedStatusGroup] = useState('All');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    url: '',
    version: 'v1.0.0',
    description: ''
  });
  const [activeUsers, setActiveUsers] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [memberProjectRoles, setMemberProjectRoles] = useState({});
  const [ownerMemberId, setOwnerMemberId] = useState('');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/applications', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const activeApps = response.data.data.filter(app => app.status === 'Active');
      setApplications(activeApps);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load applications');
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = async () => {
    try {
      const res = await axios.get('/api/auth/all-users', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const users = (res.data?.data || []).filter(u => u.hasCompletedOnboarding && (u.role === 'tester' || u.role === 'developer'));
      setActiveUsers(users);
      setShowCreateModal(true);
    } catch (error) {
      toast.error('Failed to load active users');
    }
  };

  const toggleSelectMember = (userId) => {
    setSelectedMemberIds(prev => {
      const exists = prev.includes(userId);
      const next = exists ? prev.filter(id => id !== userId) : [...prev, userId];
      if (!exists) {
        setMemberProjectRoles(prevRoles => ({ ...prevRoles, [userId]: prevRoles[userId] || 'Member' }));
      } else {
        setMemberProjectRoles(prevRoles => {
          const { [userId]: _, ...rest } = prevRoles;
          return rest;
        });
        if (ownerMemberId === userId) setOwnerMemberId('');
      }
      return next;
    });
  };

  const submitCreateProject = async () => {
    if (!createForm.name.trim() || !createForm.description.trim()) {
      toast.error('Name and Description are required');
      return;
    }
    const owners = selectedMemberIds.filter(id => memberProjectRoles[id] === 'Project Owner');
    if (owners.length < 1) {
      toast.error('Assign at least one Project Owner');
      return;
    }
    const ownerId = ownerMemberId || owners[0];
    try {
      const payload = {
        name: createForm.name,
        url: createForm.url,
        version: createForm.version,
        description: createForm.description,
        members: selectedMemberIds.map(uid => ({
          userId: uid,
          projectRole: memberProjectRoles[uid] || 'Member'
        })),
        ownerId
      };
      const res = await axios.post('/api/applications', payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (res.data?.success) {
        toast.success('Project created successfully');
        setShowCreateModal(false);
        setCreateForm({ name: '', url: '', version: 'v1.0.0', description: '' });
        setSelectedMemberIds([]);
        setMemberProjectRoles({});
        setOwnerMemberId('');
        fetchApplications();
      } else {
        toast.error(res.data?.message || 'Failed to create project');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create project');
    }
  };

  const fetchApplicationStats = async (appId, statusGroup = 'All') => {
    try {
      setStatsLoading(true);

      const response = await axios.get(`/api/manager/applications/${appId}/performance`, {
        params: { statusGroup },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const { data } = response.data;

      // Set application stats
      setAppStats(data.stats);

      // Set team members
      setTeamMembers(data.teamMembers || []);

      // Format data for the graph
      const formattedData = data.labels.map((date, index) => ({
        date,
        'Bugs Logged': data.bugsLogged[index] || 0,
        'Bugs Fixed': data.bugsFixed[index] || 0,
        'Bugs Closed': data.bugsClosed[index] || 0
      }));

      setTrendData(formattedData);

    } catch (error) {
      console.error('Failed to fetch application stats:', error);
      toast.error('Failed to load application statistics');
      setAppStats(null);
      setTrendData([]);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleApplicationChange = (value) => {
    setSelectedAppId(value);

    if (value) {
      const app = applications.find(a => a._id === value);
      setSelectedApp(app);
      fetchApplicationStats(value, selectedStatusGroup);
    } else {
      setSelectedApp(null);
      setAppStats(null);
      setTrendData(null);
      setTeamMembers([]);
    }
  };

  const handleStatusGroupChange = (value) => {
    setSelectedStatusGroup(value);
    if (selectedAppId) {
      fetchApplicationStats(selectedAppId, value);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Dropdown */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Project Management</h1>
              <p className="mt-1 text-sm text-gray-600">View and analyze project statistics</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
              >
                Create New Project
              </button>
              <div className="w-64">
                <label htmlFor="project-select" className="block text-sm font-medium text-gray-700 mb-1">
                  Select Project
                </label>
                <ScrollableDropdown
                  value={selectedAppId}
                  onChange={handleApplicationChange}
                  options={[
                    { value: '', label: 'Choose a project...' },
                    ...applications.map((app) => ({ value: app._id, label: app.name }))
                  ]}
                  placeholder="Choose a project..."
                  buttonHeightClassName="h-10"
                  maxMenuHeightPx={260}
                  maxMenuHeightClassName="max-h-72"
                />
              </div>

              <div className="w-64">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status Group
                </label>
                <ScrollableDropdown
                  value={selectedStatusGroup}
                  onChange={handleStatusGroupChange}
                  options={[
                    { value: 'All', label: 'All Status' },
                    { value: 'Active', label: 'Active' },
                    { value: 'Completed', label: 'Completed' },
                    { value: 'Invalid / Not Actionable', label: 'Invalid / Not Actionable' }
                  ]}
                  placeholder="Status Group"
                  buttonHeightClassName="h-10"
                  maxMenuHeightPx={200}
                  maxMenuHeightClassName="max-h-52"
                />
              </div>
            </div>
          </div>
        </div>

        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Create New Project</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter project name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                    <input
                      type="text"
                      value={createForm.version}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, version: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      placeholder="v1.0.0"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                    <input
                      type="url"
                      value={createForm.url}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, url: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                    rows={3}
                    placeholder="Brief description of the project"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">Select Active Members</h4>
                    <span className="text-xs text-gray-500">{selectedMemberIds.length} selected</span>
                  </div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 max-h-56 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {activeUsers.length === 0 ? (
                      <p className="text-sm text-gray-500">No active users found</p>
                    ) : (
                      activeUsers.map(user => {
                        const isSelected = selectedMemberIds.includes(user._id);
                        const role = memberProjectRoles[user._id] || 'Member';
                        return (
                          <div
                            key={user._id}
                            className={`flex items-center justify-between p-2 rounded border ${isSelected ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-gray-200'}`}
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelectMember(user._id)}
                                className="h-4 w-4 text-indigo-600 rounded"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                                <p className="text-xs text-gray-500">{user.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-40">
                                <ScrollableDropdown
                                  value={role}
                                  onChange={(value) => setMemberProjectRoles(prev => ({ ...prev, [user._id]: value }))}
                                  options={[
                                    { value: 'Project Owner', label: 'Project Owner' },
                                    { value: 'Project Manager', label: 'Project Manager' },
                                    { value: 'Team Lead', label: 'Team Lead' },
                                    { value: 'Member', label: 'Member' }
                                  ]}
                                  placeholder="Project Role"
                                  buttonHeightClassName="h-9"
                                  maxMenuHeightPx={200}
                                  maxMenuHeightClassName="max-h-52"
                                />
                              </div>
                              {memberProjectRoles[user._id] === 'Project Owner' && (
                                <label className="flex items-center text-xs gap-1">
                                  <input
                                    type="radio"
                                    name="ownerSelection"
                                    checked={ownerMemberId === user._id}
                                    onChange={() => setOwnerMemberId(user._id)}
                                    className="h-3 w-3"
                                  />
                                  Owner
                                </label>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Assign at least one Project Owner. You may designate one as the primary Owner.</p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitCreateProject}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
                >
                  Create Project
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedApp ? (
          <>
            {/* Project Details Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Application Name</dt>
                        <dd
                          className="text-lg font-medium text-gray-900 cursor-pointer hover:text-indigo-600 hover:underline"
                          onClick={() => window.location.href = `/applications/${selectedApp._id}/timeline`}
                        >
                          {selectedApp.name}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0l1 16h8l1-16M9 10h6" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">Version</dt>
                        <dd className="text-lg font-medium text-gray-900">{selectedApp.version}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">URL</dt>
                        <dd className="text-lg font-medium text-gray-900 truncate">{selectedApp.url || 'N/A'}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Team Members Section */}
            <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Team Members</h2>
                <p className="text-sm text-gray-500 mt-1">People working on {selectedApp.name}</p>
              </div>
              <div className="p-6">
                {teamMembers.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {teamMembers.map((member) => (
                      <div key={member._id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-semibold text-sm">
                                {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-3 flex-1">
                            <p className="text-sm font-medium text-gray-900">{member.name}</p>
                            <p className="text-xs text-gray-500">{member.email}</p>
                            <div className="mt-2 flex items-center justify-between">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${member.role === 'developer'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-purple-100 text-purple-800'
                                }`}>
                                {member.role === 'developer' ? '👨‍💻 Developer' : '🧪 Tester'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {member.bugsCount} {member.bugsCount === 1 ? 'bug' : 'bugs'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="mt-2">No team members found for this application</p>
                  </div>
                )}
              </div>
            </div>

            {/* Bug Statistics Table */}
            <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Bug Statistics</h2>
                <p className="text-sm text-gray-500 mt-1">Current bug status for {selectedApp.name}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Metric
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Count
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">1</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Total Bugs Logged</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="text-red-600 font-semibold">{appStats?.bugsLogged || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Active
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">2</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Bugs Pending</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="text-yellow-600 font-semibold">{appStats?.bugsPending || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">3</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Bugs Deferred</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="text-yellow-600 font-semibold">{appStats?.bugsDeferred || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Deferred
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">4</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Bugs Fixed</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="text-green-600 font-semibold">{appStats?.bugsFixed || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Resolved
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">5</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Bugs Closed</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="text-blue-600 font-semibold">{appStats?.bugsClosed || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          Closed
                        </span>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">6</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Bugs Open</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="text-orange-600 font-semibold">{appStats?.bugsOpen || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                          Open
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 30-Day Project Analytics Graph */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">30-Day Project Analytics</h2>
                <p className="text-sm text-gray-500 mt-1">Daily bug trends and activity for the last 30 days</p>
              </div>
              <div className="p-6">
                {statsLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : trendData ? (
                  <PerformanceGraph data={trendData} />
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    No analytics data available
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="bg-white shadow rounded-lg p-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Project Selected</h3>
            <p className="mt-2 text-sm text-gray-500">
              Select a project from the dropdown to view project analytics and team details
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectList;
